import React from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { grey } from '@mui/material/colors'

import Loader from 'components/Shared/Loader'
import { ParticipantProfileContext } from 'containers/ParticipantProfile'
import ParticipantCard from 'components/ParticipantProfile/ParticipantCard'
import StatusBadge from 'components/ParticipantProfile/StatusBadge'
import Chip from 'components/Shared/Chip'
import AlleySpacer from 'components/Shared/AlleySpacer'

const ParticipantSummary = () => {
  const { participantSummary, loading } = React.useContext(
    ParticipantProfileContext,
  ).state
  const { t } = useTranslation()
  if (loading) return <Loader />

  const {
    unsubscribed,
    lastSeenAt,
    createdAt,
    email,
    status,
    login,
    type,
    blocked,
    unconfirmedEmail,
  } = participantSummary
  const subscriptionStatus = unsubscribed ? 'unSubscribed' : 'subscribed'

  const formatandRenderLastSeen = () => {
    if (lastSeenAt) {
      const lastSeenDate = new Date(lastSeenAt)
      const formattedLastSeen = moment(lastSeenDate, 'YYYYMMDD').fromNow()

      return formattedLastSeen
    }
    return t('participant.grid.notSeen')
  }

  return (
    <ParticipantCard flexGrow={1}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="0.625rem"
          >
            <Typography
              variant="h5"
              component="h5"
              data-testid="summary-header"
            >
              {t('participant.summary.header')}
            </Typography>
            <StatusBadge blocked={blocked} type={type} status={status} />
            {unconfirmedEmail ? (
              <Chip
                size="small"
                sx={{ backgroundColor: grey[700], color: grey[50] }}
                filterValues={[t(`participant.status.unconfirmed`)]}
              />
            ) : null}
          </Box>
        </Box>
      </Stack>
      <AlleySpacer direction={'top'} spacing={3}>
        <Box
          gridTemplateColumns="repeat(12, 1fr)"
          gap={{ xs: 4, md: 3 }}
          sx={{
            display: {
              xs: 'flex',
              sm: 'grid',
            },
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
          }}
        >
          <Box gridColumn="span 2">
            <TextField
              id="standard-read-only-input"
              label={t('participant.summary.login')}
              value={login || ''}
              InputProps={{
                readOnly: true,
              }}
              variant="standard"
              data-testid="summary-login"
              fullWidth
            />
          </Box>

          <Box gridColumn="span 4">
            <TextField
              id="standard-read-only-input"
              label={t('participant.summary.emailAddress')}
              value={email || ''}
              InputProps={{
                readOnly: true,
              }}
              variant="standard"
              fullWidth
              data-testid="summary-email"
            />
          </Box>

          <Box gridColumn="span 2">
            <TextField
              id="standard-read-only-input"
              label={t('participant.summary.newsletter')}
              value={t(`participant.summary.${subscriptionStatus}`) || ''}
              InputProps={{
                readOnly: true,
              }}
              variant="standard"
              data-testid="summary-subscribed"
              fullWidth
            />
          </Box>
          <Box gridColumn="span 2">
            <TextField
              id="standard-read-only-input"
              label={t('participant.summary.lastSeen')}
              value={formatandRenderLastSeen() || ''}
              InputProps={{
                readOnly: true,
              }}
              variant="standard"
              data-testid="summary-last-seen"
              fullWidth
            />
          </Box>

          <Box gridColumn="span 2">
            <TextField
              id="standard-read-only-input"
              label={t('participant.summary.joinedAt')}
              value={moment(createdAt, 'YYYYMMDD').format('DD MMM YYYY') || ''}
              InputProps={{
                readOnly: true,
              }}
              variant="standard"
              data-testid="summary-joined"
              fullWidth
            />
          </Box>
        </Box>
      </AlleySpacer>
    </ParticipantCard>
  )
}

export default ParticipantSummary
